/**
 * The sign-in screens (Phase 5b, step 4): the sign-in, sign-up and recovery
 * forms, the dialogs that unlock the vault with the recovery phrase and show
 * a new phrase, and the prompt for accounts without a recovery check. The
 * account's client (keys, server calls, the session) is the engine's
 * (engine/auth/handlers.js); these read the forms, show the steps on the buttons
 * and the errors, as before the split.
 */
import * as Auth from "../../../../engine/auth/handlers.js";
import { promptForPassword } from "./password-prompt.js";
import { escapeHtml } from "../../../../engine/core/validation.js";
import { t } from "../../../../shared/i18n/index.js";

function showAuthError(message, errorId = "login-error") {
  const el = document.getElementById(errorId);
  if (el) {
    el.textContent = message;
    el.classList.remove("hidden");
  }
}

// The client's steps, as the buttons say them.
const STAGE_TEXT = {
  "signing-in": "account.login.signingIn",
  unlocking: "account.login.unlockingVault",
  "generating-keys": "account.signup.generatingKeys",
  creating: "account.signup.creating",
  fetching: "account.recover.fetching",
  decrypting: "account.recover.decrypting",
  reencrypting: "account.recover.reencrypting",
  updating: "account.recover.updating",
};

const showStageOn = (button) => (stage) => {
  if (button && STAGE_TEXT[stage]) button.textContent = t(STAGE_TEXT[stage]);
};

/**
 * Handle login - Complete flow with E2EE unlocking
 */
export async function handleLogin(e) {
  e.preventDefault();
  const username = document.getElementById("login-username").value;
  const passwordInput = document.getElementById("login-password");
  const password = passwordInput.value;
  passwordInput.value = ""; // Clear password from DOM immediately

  const submitBtn = e.target.querySelector('button[type="submit"]');
  try {
    const originalText = submitBtn.textContent;
    submitBtn.disabled = true;
    const outcome = await Auth.signIn(username, password, { onStage: showStageOn(submitBtn) });
    submitBtn.textContent = originalText;
    submitBtn.disabled = false;
    if (outcome === "needs-recovery-phrase") {
      // The password signs in but no longer opens the vault.
      showUnlockModal();
      return;
    }
    await Auth.completeSignIn();
  } catch (err) {
    showAuthError(err.message);
    if (submitBtn) {
      submitBtn.textContent = t("auth.login.submit");
      submitBtn.disabled = false;
    }
  }
}

/**
 * Handle signup - Complete flow with key generation and recovery phrase
 */
export async function handleRegister(e) {
  e.preventDefault();
  const username = document.getElementById("register-username").value;
  const email = document.getElementById("register-email").value.trim() || null;
  const password = document.getElementById("register-password").value;
  const confirmPassword = document.getElementById("register-confirm").value;

  if (password !== confirmPassword) {
    showAuthError(t("account.password.mismatch"), "signup-error");
    return;
  }

  const submitBtn = e.target.querySelector('button[type="submit"]');
  try {
    const originalText = submitBtn.textContent;
    submitBtn.disabled = true;
    const mnemonic = await Auth.register({ username, email, password }, { onStage: showStageOn(submitBtn) });
    submitBtn.textContent = originalText;
    submitBtn.disabled = false;
    // The new account's recovery phrase, then the app.
    showRecoveryMnemonic(mnemonic, () => Auth.completeSignIn({ isNewUser: true }));
  } catch (err) {
    showAuthError(err.message, "signup-error");
    if (submitBtn) {
      submitBtn.textContent = t("auth.signup.submit");
      submitBtn.disabled = false;
    }
  }
}

/**
 * Handle account recovery with mnemonic
 */
export async function handleRecover(e) {
  e.preventDefault();
  const username = document.getElementById("recover-username").value.trim();
  const mnemonic = document.getElementById("recover-mnemonic").value.trim();
  const newPassword = document.getElementById("recover-new-password").value;
  const confirmPassword = document.getElementById("recover-confirm-password").value;

  if (!username) {
    showAuthError(t("account.recover.usernameRequired"), "recover-error");
    return;
  }
  if (newPassword !== confirmPassword) {
    showAuthError(t("account.password.mismatch"), "recover-error");
    return;
  }

  const submitBtn = e.target.querySelector('button[type="submit"]');
  const originalText = submitBtn.textContent;
  try {
    submitBtn.disabled = true;
    await Auth.recover({ username, mnemonic, newPassword }, { onStage: showStageOn(submitBtn) });
    submitBtn.textContent = originalText;
    submitBtn.disabled = false;
    await Auth.completeSignIn();
  } catch (err) {
    showAuthError(err.message, "recover-error");
    submitBtn.textContent = originalText;
    submitBtn.disabled = false;
  }
}

/**
 * Show unlock modal for recovery phrase entry (when password fails to decrypt)
 */
function showUnlockModal() {
  const modal = document.createElement("div");
  modal.className = "unlock-modal";
  modal.id = "unlock-modal";
  modal.innerHTML = `
    <div class="unlock-modal-content">
      <button class="unlock-close-btn" id="unlock-close" title="${escapeHtml(t("common.cancel"))}">&times;</button>
      <h2>${escapeHtml(t("account.unlock.title"))}</h2>
      <p>${escapeHtml(t("account.unlock.intro"))}</p>
      <div class="unlock-error hidden" id="unlock-error"></div>
      <form class="unlock-form" id="unlock-form">
        <textarea id="unlock-recovery" placeholder="${escapeHtml(t("auth.recover.phrase"))}" rows="3"></textarea>
        <div style="display:flex;gap:10px;">
          <button type="button" class="auth-btn" id="unlock-cancel" style="background:#6b7280;">${escapeHtml(t("common.cancel"))}</button>
          <button type="submit" class="auth-btn" style="flex:1;">${escapeHtml(t("account.unlock.submit"))}</button>
        </div>
      </form>
    </div>
  `;
  document.body.appendChild(modal);

  // Cancel buttons - logout and close modal
  const closeModal = async () => {
    modal.remove();
    await Auth.logout();
  };
  document.getElementById("unlock-close").addEventListener("click", closeModal);
  document.getElementById("unlock-cancel").addEventListener("click", closeModal);

  document.getElementById("unlock-form").addEventListener("submit", async (e) => {
    e.preventDefault();
    const mnemonic = document.getElementById("unlock-recovery").value.trim();
    const errorEl = document.getElementById("unlock-error");
    const submitBtn = e.target.querySelector('button[type="submit"]');

    if (!mnemonic) {
      errorEl.textContent = t("account.unlock.phraseRequired");
      errorEl.classList.remove("hidden");
      return;
    }

    submitBtn.textContent = t("account.unlock.unlocking");
    submitBtn.disabled = true;

    try {
      await Auth.unlockWithRecoveryPhrase(mnemonic);
      modal.remove();
      await Auth.completeSignIn();
    } catch (err) {
      errorEl.textContent = err.message;
      errorEl.classList.remove("hidden");
      submitBtn.textContent = t("account.unlock.submit");
      submitBtn.disabled = false;
    }
  });
}

/**
 * Show a recovery mnemonic in the save-phrase modal.
 *
 * @param {string} mnemonic  space-separated 12-word phrase
 * @param {() => void|Promise<void>} onContinue  runs on Continue (sign-up:
 *   the app opens; a new phrase while signed in: nothing more)
 */
function showRecoveryMnemonic(mnemonic, onContinue) {
  const modal = document.createElement("div");
  modal.className = "recovery-modal";
  modal.innerHTML = `
    <div class="recovery-modal-content">
      <h2>${escapeHtml(t("account.phrase.saveTitle"))}</h2>
      <p class="recovery-warning">
        <strong>${escapeHtml(t("account.phrase.important"))}</strong> ${escapeHtml(t("account.phrase.warning"))}
      </p>
      <div class="recovery-words">
        ${mnemonic
          .split(" ")
          .map(
            (word, i) =>
              `<div class="recovery-word"><span class="word-num">${i + 1}.</span> ${escapeHtml(word)}</div>`,
          )
          .join("")}
      </div>
      <div class="recovery-actions">
        <button class="btn btn-secondary" id="copy-recovery">${escapeHtml(t("account.phrase.copy"))}</button>
        <label class="recovery-confirm">
          <input type="checkbox" id="recovery-confirmed" />
          ${escapeHtml(t("account.phrase.confirm"))}
        </label>
        <button class="btn btn-primary" id="recovery-continue" disabled>${escapeHtml(t("common.continue"))}</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  // Copy button (auto-clears clipboard after 30 seconds for security)
  document.getElementById("copy-recovery").addEventListener("click", () => {
    navigator.clipboard.writeText(mnemonic);
    document.getElementById("copy-recovery").textContent = t("account.phrase.copied");
    setTimeout(() => {
      const btn = document.getElementById("copy-recovery");
      if (btn) btn.textContent = t("account.phrase.copy");
    }, 2000);
    // Clear clipboard after 30 seconds to prevent accidental leakage
    setTimeout(() => {
      navigator.clipboard.writeText("").catch(() => {});
    }, 30000);
  });

  // Checkbox enables continue
  document.getElementById("recovery-confirmed").addEventListener("change", (e) => {
    document.getElementById("recovery-continue").disabled = !e.target.checked;
  });

  // Continue button
  document.getElementById("recovery-continue").addEventListener("click", async () => {
    modal.remove();
    await onContinue();
  });
}

/**
 * Regenerate the account's recovery phrase (signed in): the current
 * password first (engine/auth/handlers.js replaceRecoveryPhrase says why), then the
 * new phrase shown.
 *
 * Throws when not signed in (caller shows the error).
 * @returns {Promise<boolean>} false when the user cancelled
 */
export async function regenerateRecoveryPhrase() {
  if (!Auth.hasVaultKey()) {
    throw new Error(t("account.phrase.loginRequired"));
  }

  const mnemonic = await promptForPassword({
    title: t("account.phrase.newTitle"),
    message: t("account.phrase.newMessage"),
    submitLabel: t("account.phrase.generate"),
    busyLabel: t("account.phrase.generating"),
    action: (password) => Auth.replaceRecoveryPhrase(password),
  });
  if (!mnemonic) return false;

  // Show the new phrase (display-only; the user is already authenticated).
  showRecoveryMnemonic(mnemonic, () => {});
  return true;
}

const RECOVERY_UPGRADE_DISMISS_KEY = "orbital_recovery_upgrade_dismissed";

/**
 * Where the user can find the regenerate action later. The two shells put it
 * in different places, and naming a path that doesn't exist on the device in
 * front of the user makes "Remind me later" look like a dead end.
 */
function recoveryPathLabel() {
  const onMobileShell =
    typeof document !== "undefined" &&
    document.body?.classList.contains("mobile-shell-active");
  return onMobileShell
    ? t("account.phrase.pathMobile")
    : t("account.phrase.pathDesktop");
}

/** HTML of `key`'s text with its `{name}` placeholder replaced by `bold` in bold. */
function textWithBold(key, name, bold) {
  const [before, after = ""] = t(key, { [name]: "\u0000" }).split("\u0000");
  return `${escapeHtml(before)}<strong>${escapeHtml(bold)}</strong>${escapeHtml(after)}`;
}

/**
 * Proactively prompt an account that has no stored recovery_verifier to
 * regenerate its recovery phrase. Legacy accounts (created before the C-1
 * hardening) can no longer use /auth/recover until they re-save recovery data;
 * this nudge closes that gap without waiting for the user to hit the wall.
 *
 * Shown once per session on login; "Remind me later" suppresses it only for
 * the current session, so it re-appears on the next login until the user
 * actually regenerates (after which the server reports has_recovery_verifier
 * true and it never shows again).
 *
 * @param {Object} envelope  envelope from GET /api/e2ee/envelope, which now
 *   carries `has_recovery_verifier`
 */
export function maybeShowRecoveryUpgradePrompt(envelope) {
  const env = envelope || Auth.getE2eeEnvelope();
  // Only accounts the server explicitly reports as verifier-less. An absent
  // field (e.g. a freshly-built envelope right after signup) is treated as
  // "already has one" — no prompt.
  if (!env || env.has_recovery_verifier !== false) return;

  try {
    if (sessionStorage.getItem(RECOVERY_UPGRADE_DISMISS_KEY) === "1") return;
  } catch {
    /* sessionStorage unavailable — fall through and show it */
  }

  // Don't stack on top of an already-open recovery modal.
  if (document.querySelector(".recovery-modal")) return;

  const modal = document.createElement("div");
  modal.className = "recovery-modal";
  modal.id = "recovery-upgrade-modal";
  modal.innerHTML = `
    <div class="recovery-modal-content">
      <h2>${escapeHtml(t("account.recoveryUpgrade.title"))}</h2>
      <p class="recovery-warning">
        ${textWithBold("account.recoveryUpgrade.intro", "emphasis", t("account.recoveryUpgrade.introEmphasis"))}
      </p>
      <p>
        ${textWithBold("account.recoveryUpgrade.explain", "button", t("account.recoveryUpgrade.now"))}
      </p>
      <p class="recovery-upgrade-note">
        ${textWithBold("account.recoveryUpgrade.laterNote", "path", recoveryPathLabel())}
      </p>
      <div class="recovery-actions">
        <button class="btn btn-secondary" id="recovery-upgrade-later">${escapeHtml(t("account.recoveryUpgrade.later"))}</button>
        <button class="btn btn-primary" id="recovery-upgrade-now">${escapeHtml(t("account.recoveryUpgrade.now"))}</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  document.getElementById("recovery-upgrade-later").addEventListener("click", () => {
    try {
      sessionStorage.setItem(RECOVERY_UPGRADE_DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    modal.remove();
  });

  document.getElementById("recovery-upgrade-now").addEventListener("click", async () => {
    const nowBtn = document.getElementById("recovery-upgrade-now");
    const laterBtn = document.getElementById("recovery-upgrade-later");
    nowBtn.disabled = true;
    laterBtn.disabled = true;
    nowBtn.textContent = t("account.recoveryUpgrade.preparing");
    // Close this prompt; regenerateRecoveryPhrase opens the save-your-phrase
    // modal (12 words + copy + "I have saved it" confirmation).
    modal.remove();
    try {
      await regenerateRecoveryPhrase();
    } catch (err) {
      console.error("[Recovery] Upgrade prompt regenerate failed:", err);
      window.alert(t("account.recoveryUpgrade.failed", {
        error: err?.message || String(err),
        path: t("account.phrase.pathDesktop"),
      }));
    }
  });
}
